/**
 * Payment Provider Management Component
 * Admin panel for managing Razorpay and Stripe payment providers
 */

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  CreditCard, 
  IndianRupee, 
  DollarSign, 
  Settings, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Users
} from 'lucide-react'
import { adminApi } from '@/admin/services/api'
import { toast } from 'sonner'

interface PaymentStats {
  totalTransactions: number
  successfulTransactions: number
  failedTransactions: number
  totalRevenue: number
  providerBreakdown: {
    stripe: number
    razorpay: number
  }
}

interface PaymentTransaction {
  id: string
  provider: 'stripe' | 'razorpay'
  amount: number
  currency: string
  status: 'pending' | 'completed' | 'failed' | 'refunded'
  tenantId: string
  tenantName: string
  createdAt: string
  method: string
  fee: number
}

export function PaymentProviderManagement() {
  const [stats, setStats] = useState<PaymentStats | null>(null)
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [providerSettings, setProviderSettings] = useState({
    stripe: { enabled: true, testMode: true },
    razorpay: { enabled: true, testMode: true }
  })

  useEffect(() => {
    loadPaymentData()
  }, [])

  const loadPaymentData = async () => {
    try {
      setLoading(true)
      
      const response = await adminApi.getBillingOperations()
      if (response.success && response.data) {
        const ops = response.data.map((op: any) => ({
          id: op.id,
          provider: 'stripe', // Mocking since DB only holds stripe metadata for now
          amount: Number(op.amount) || 0,
          currency: op.currency || 'USD',
          status: op.status,
          tenantId: op.tenant_id,
          tenantName: op.tenant_id.substring(0, 8), // Placeholder
          createdAt: op.created_at,
          method: 'card',
          fee: (Number(op.amount) || 0) * 0.029 + 0.30
        })) as PaymentTransaction[];

        setTransactions(ops);

        const totalTransactions = ops.length;
        const successfulTransactions = ops.filter(t => t.status === 'completed').length;
        const failedTransactions = ops.filter(t => t.status === 'failed').length;
        const totalRevenue = ops.filter(t => t.status === 'completed').reduce((acc, t) => acc + t.amount, 0);
        
        setStats({
          totalTransactions,
          successfulTransactions,
          failedTransactions,
          totalRevenue,
          providerBreakdown: {
            stripe: totalTransactions,
            razorpay: 0
          }
        });
      }
    } catch (error) {
      console.error('Failed to load payment data:', error)
      toast.error('Failed to load payment data')
    } finally {
      setLoading(false)
    }
  }

  const handleProviderToggle = (provider: 'stripe' | 'razorpay', enabled: boolean) => {
    setProviderSettings(prev => ({
      ...prev,
      [provider]: { ...prev[provider], enabled }
    }))
    
    // In production, this would update the backend
    toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} ${enabled ? 'enabled' : 'disabled'}`)
  }

  const handleTestModeToggle = (provider: 'stripe' | 'razorpay', testMode: boolean) => {
    setProviderSettings(prev => ({
      ...prev,
      [provider]: { ...prev[provider], testMode }
    }))
    
    toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} test mode ${testMode ? 'enabled' : 'disabled'}`)
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>
      case 'refunded':
        return <Badge className="bg-blue-100 text-blue-800">Refunded</Badge>
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>
    }
  }

  const getProviderIcon = (provider: string) => {
    return provider === 'razorpay' ? 
      <IndianRupee className="h-4 w-4" /> : 
      <DollarSign className="h-4 w-4" />
  }

  const successRate = stats ? 
    (stats.successfulTransactions / stats.totalTransactions * 100).toFixed(1) : 
    '0'

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium">Payment Providers</h2>
          <p className="text-muted-foreground">Manage payment providers and monitor transactions</p>
        </div>
        <Button onClick={loadPaymentData} variant="outline" size="sm">
          <Settings className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-medium">
                  {stats ? formatCurrency(stats.totalRevenue, 'USD') : '$0'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-medium">{successRate}%</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Transactions</p>
                <p className="text-2xl font-medium">{stats?.totalTransactions || 0}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Failed Transactions</p>
                <p className="text-2xl font-medium">{stats?.failedTransactions || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Provider Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Provider Breakdown</CardTitle>
                <CardDescription>Transaction volume by payment provider</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="h-4 w-4" />
                      <span className="font-medium">Razorpay</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{stats?.providerBreakdown.razorpay || 0}</div>
                      <div className="text-sm text-muted-foreground">
                        {stats ? ((stats.providerBreakdown.razorpay / stats.totalTransactions * 100).toFixed(1)) : '0'}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <span className="font-medium">Stripe</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{stats?.providerBreakdown.stripe || 0}</div>
                      <div className="text-sm text-muted-foreground">
                        {stats ? ((stats.providerBreakdown.stripe / stats.totalTransactions * 100).toFixed(1)) : '0'}%
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.slice(0, 5).map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {getProviderIcon(transaction.provider)}
                        <div>
                          <div className="font-medium">{transaction.tenantName}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(transaction.status)}
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="providers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Razorpay */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="h-5 w-5" />
                  Razorpay
                </CardTitle>
                <CardDescription>Indian payment gateway with UPI support</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="razorpay-enabled">Enable Razorpay</Label>
                  <Switch
                    id="razorpay-enabled"
                    checked={providerSettings.razorpay.enabled}
                    onCheckedChange={(checked) => handleProviderToggle('razorpay', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="razorpay-test">Test Mode</Label>
                  <Switch
                    id="razorpay-test"
                    checked={providerSettings.razorpay.testMode}
                    onCheckedChange={(checked) => handleTestModeToggle('razorpay', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razorpay-key">API Key ID</Label>
                  <Input
                    id="razorpay-key"
                    type="password"
                    placeholder="rzp_test_..."
                    defaultValue="rzp_test_xxxxx_replace_me"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razorpay-secret">API Key Secret</Label>
                  <Input
                    id="razorpay-secret"
                    type="password"
                    placeholder="••••••••••••••••"
                  />
                </div>

                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div>• Supports: Card, UPI, Net Banking, Wallet</div>
                    <div>• Processing fee: 2.0%</div>
                    <div>• Settlement: T+2 days</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stripe */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Stripe
                </CardTitle>
                <CardDescription>Global payment gateway with extensive features</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="stripe-enabled">Enable Stripe</Label>
                  <Switch
                    id="stripe-enabled"
                    checked={providerSettings.stripe.enabled}
                    onCheckedChange={(checked) => handleProviderToggle('stripe', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="stripe-test">Test Mode</Label>
                  <Switch
                    id="stripe-test"
                    checked={providerSettings.stripe.testMode}
                    onCheckedChange={(checked) => handleTestModeToggle('stripe', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe-key">Publishable Key</Label>
                  <Input
                    id="stripe-key"
                    type="password"
                    placeholder="pk_test_..."
                    defaultValue="pk_test_xxxxx_replace_me"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe-secret">Secret Key</Label>
                  <Input
                    id="stripe-secret"
                    type="password"
                    placeholder="••••••••••••••••"
                  />
                </div>

                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div>• Supports: Card, Apple Pay, Google Pay</div>
                    <div>• Processing fee: 2.9% + $0.30</div>
                    <div>• Settlement: T+7 days</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>All Transactions</CardTitle>
              <CardDescription>Complete transaction history</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Fee</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-medium">{transaction.tenantName}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getProviderIcon(transaction.provider)}
                          <span className="capitalize">{transaction.provider}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(transaction.amount, transaction.currency)}</TableCell>
                      <TableCell className="capitalize">{transaction.method}</TableCell>
                      <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                      <TableCell>{new Date(transaction.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell>{formatCurrency(transaction.fee, transaction.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Payment provider settings require careful configuration. Test thoroughly before enabling in production.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Webhook Configuration</CardTitle>
                <CardDescription>Configure webhook endpoints for payment notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="razorpay-webhook">Razorpay Webhook URL</Label>
                  <Input
                    id="razorpay-webhook"
                    value="https://dew.dcodeinteriors.com/functions/v1/razorpay-webhook"
                    readOnly
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stripe-webhook">Stripe Webhook URL</Label>
                  <Input
                    id="stripe-webhook"
                    value="https://dew.dcodeinteriors.com/functions/v1/stripe-webhook"
                    readOnly
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Configure security and compliance settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="fraud-detection">Fraud Detection</Label>
                    <p className="text-sm text-muted-foreground">Enable advanced fraud detection</p>
                  </div>
                  <Switch id="fraud-detection" defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="pci-compliance">PCI Compliance</Label>
                    <p className="text-sm text-muted-foreground">Maintain PCI DSS compliance</p>
                  </div>
                  <Switch id="pci-compliance" defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="audit-logging">Audit Logging</Label>
                    <p className="text-sm text-muted-foreground">Log all payment transactions</p>
                  </div>
                  <Switch id="audit-logging" defaultChecked />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
